// Phase 2 hooks: production bugs, action items, learnings, bug review, regression suite.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorException, del, get, patch, post, put, uploadToPresignedUrl } from "./client";
import { MAX_ATTACHMENT_BYTES, taskKey } from "./testing";
import type { ActionItemView } from "./types/ActionItemView";
import type { ActivityEntry } from "./types/ActivityEntry";
import type { AddBugLinkRequest } from "./types/AddBugLinkRequest";
import type { AddGitlabLinkRequest } from "./types/AddGitlabLinkRequest";
import type { BugAttachment } from "./types/BugAttachment";
import type { BugAttachmentUpload } from "./types/BugAttachmentUpload";
import type { BugComment } from "./types/BugComment";
import type { BugDetail } from "./types/BugDetail";
import type { BugGitlabDetail } from "./types/BugGitlabDetail";
import type { BugReview } from "./types/BugReview";
import type { BugSighting } from "./types/BugSighting";
import type { BugSightings } from "./types/BugSightings";
import type { BugStatus } from "./types/BugStatus";
import type { BugSummary } from "./types/BugSummary";
import type { BugTransitionRequest } from "./types/BugTransitionRequest";
import type { CaseView } from "./types/CaseView";
import type { CreateActionItemRequest } from "./types/CreateActionItemRequest";
import type { CreateBugRequest } from "./types/CreateBugRequest";
import type { CreateSightingRequest } from "./types/CreateSightingRequest";
import type { DecideRegressionRequest } from "./types/DecideRegressionRequest";
import type { EscapePoint } from "./types/EscapePoint";
import type { GitlabLinkView } from "./types/GitlabLinkView";
import type { LearningEntry } from "./types/LearningEntry";
import type { RootCauseCategory } from "./types/RootCauseCategory";
import type { SetSightingAppRequest } from "./types/SetSightingAppRequest";
import type { Severity } from "./types/Severity";
import type { TaskDetail } from "./types/TaskDetail";
import type { UpdateActionItemRequest } from "./types/UpdateActionItemRequest";
import type { UpdateBugRequest } from "./types/UpdateBugRequest";

const enc = encodeURIComponent;

export const bugsPath = (projectKey: string) => `/projects/${enc(projectKey)}/bugs`;
export const bugPath = (projectKey: string, n: number) => `${bugsPath(projectKey)}/${n}`;
export const bugKey = (projectKey: string, n: number) => ["bug", projectKey, n] as const;

/** After any change to a bug: its detail, every list, review and inbox. */
function useBugInvalidate(projectKey: string, n: number) {
  const qc = useQueryClient();
  return (detail?: BugDetail) => {
    if (detail) qc.setQueryData([...bugKey(projectKey, n), "detail"], detail);
    void qc.invalidateQueries({ queryKey: bugKey(projectKey, n) });
    void qc.invalidateQueries({ queryKey: ["bugs", projectKey] });
    void qc.invalidateQueries({ queryKey: ["inbox"] });
  };
}

// ───────────── Lists ─────────────

export type BugFilters = {
  open?: boolean;
  status?: BugStatus[];
  severity?: Severity[];
  component?: string;
  /** An app's id: bugs with a sighting on it. */
  app?: string;
  /** Part of the client's username. */
  client?: string;
  mine?: boolean;
  q?: string;
};

export function useBugs(projectKey: string, f: BugFilters) {
  const params = new URLSearchParams();
  if (f.open) params.set("open", "true");
  f.status?.forEach((s) => params.append("status", s));
  f.severity?.forEach((s) => params.append("severity", s));
  if (f.component) params.set("component", f.component);
  if (f.app) params.set("app", f.app);
  if (f.client?.trim()) params.set("client", f.client.trim());
  if (f.mine) params.set("owner", "me");
  if (f.q?.trim()) params.set("q", f.q.trim());
  const qs = params.toString();
  return useQuery({
    queryKey: ["bugs", projectKey, "list", f],
    queryFn: () => get<BugSummary[]>(`${bugsPath(projectKey)}${qs ? `?${qs}` : ""}`),
    placeholderData: (prev) => prev,
  });
}

/** Open S1/S2 bugs — for the nav badge. */
export function useOpenMajorBugCount(projectKey: string | undefined) {
  const q = useQuery({
    queryKey: ["bugs", projectKey, "openMajor"],
    queryFn: () => get<BugSummary[]>(`${bugsPath(projectKey!)}?open=true&severity=s1&severity=s2`),
    enabled: !!projectKey,
    staleTime: 60_000,
  });
  return q.data?.length ?? 0;
}

export function useSimilarBugs(projectKey: string, title: string) {
  const q = title.trim();
  return useQuery({
    queryKey: ["bugs", projectKey, "similar", q],
    queryFn: () => get<BugSummary[]>(`${bugsPath(projectKey)}/similar?q=${enc(q)}`),
    enabled: q.length >= 4,
    staleTime: 60_000,
  });
}

export function useCreateBug(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBugRequest) => post<BugDetail>(bugsPath(projectKey), body),
    onSuccess: (bug) => {
      qc.setQueryData([...bugKey(projectKey, bug.number), "detail"], bug);
      void qc.invalidateQueries({ queryKey: ["bugs", projectKey] });
      void qc.invalidateQueries({ queryKey: ["inbox"] });
    },
  });
}

// ───────────── One bug ─────────────

export function useBug(projectKey: string, n: number) {
  return useQuery({
    queryKey: [...bugKey(projectKey, n), "detail"],
    queryFn: () => get<BugDetail>(bugPath(projectKey, n)),
    enabled: Number.isFinite(n),
  });
}

export function useUpdateBug(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<UpdateBugRequest>) => patch<BugDetail>(bugPath(projectKey, n), body),
    onSuccess: (bug, body) => {
      invalidate(bug);
      if (body.client) void qc.invalidateQueries({ queryKey: ["bugs", projectKey, "clients"] });
    },
  });
}

/** Clients already named on this project's bugs, most recent first (for the client field's suggestions). */
export function useBugClients(projectKey: string, q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: ["bugs", projectKey, "clients", query],
    queryFn: () =>
      get<string[]>(`${bugsPath(projectKey)}/clients${query ? `?q=${encodeURIComponent(query)}` : ""}`),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}

export function useBugTransition(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: BugTransitionRequest) => post<BugDetail>(`${bugPath(projectKey, n)}/transition`, body),
    onSuccess: invalidate,
  });
}

export function useApprovePostmortem(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: () => post<BugDetail>(`${bugPath(projectKey, n)}/postmortem/approve`),
    onSuccess: invalidate,
  });
}

export function useAddBugLink(projectKey: string, n: number) {
  const qc = useQueryClient();
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: AddBugLinkRequest) => post<BugDetail>(`${bugPath(projectKey, n)}/links`, body),
    onSuccess: (d, body) => {
      invalidate(d);
      // Links are symmetric.
      void qc.invalidateQueries({ queryKey: bugKey(projectKey, body.number) });
    },
  });
}

export function useRemoveBugLink(projectKey: string, n: number) {
  const qc = useQueryClient();
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (other: number) => del<BugDetail>(`${bugPath(projectKey, n)}/links/${other}`),
    onSuccess: (d, other) => {
      invalidate(d);
      void qc.invalidateQueries({ queryKey: bugKey(projectKey, other) });
    },
  });
}

export function useAddActionItem(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: CreateActionItemRequest) => post<BugDetail>(`${bugPath(projectKey, n)}/action-items`, body),
    onSuccess: invalidate,
  });
}

export function useUpdateActionItem(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UpdateActionItemRequest> }) =>
      patch<BugDetail>(`${bugPath(projectKey, n)}/action-items/${enc(id)}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteActionItem(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (id: string) => del<BugDetail>(`${bugPath(projectKey, n)}/action-items/${enc(id)}`),
    onSuccess: invalidate,
  });
}

export function useAddComment(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: string) => post<BugComment>(`${bugPath(projectKey, n)}/comments`, { body }),
    onSuccess: () => invalidate(),
  });
}

/** Creates a regression-suite case from the bug's reproduction steps. */
export function useCreateRegressionCase(projectKey: string, n: number) {
  const qc = useQueryClient();
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: () => post<CaseView>(`${bugPath(projectKey, n)}/regression-case`),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: taskKey(projectKey, "REGRESSION") });
    },
  });
}

/** Uploads one file to a bug: reserve, PUT to storage, mark complete. */
export async function uploadBugAttachment(projectKey: string, n: number, file: File): Promise<BugAttachment> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw new ApiErrorException("validation", "", 422);
  const contentType = file.type || "application/octet-stream";
  const target = await post<BugAttachmentUpload>(`${bugPath(projectKey, n)}/attachments`, {
    filename: file.name || "pasted-image.png",
    content_type: contentType,
    size: file.size,
  });
  await uploadToPresignedUrl(target.upload_url, file, contentType);
  return post<BugAttachment>(`${bugPath(projectKey, n)}/attachments/${enc(target.attachment.id)}/complete`);
}

export function useUploadBugAttachment(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (file: File) => uploadBugAttachment(projectKey, n, file),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteBugAttachment(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (id: string) => del(`${bugPath(projectKey, n)}/attachments/${enc(id)}`),
    onSuccess: () => invalidate(),
  });
}

export function useBugActivity(projectKey: string, n: number) {
  return useQuery({
    queryKey: [...bugKey(projectKey, n), "activity"],
    queryFn: () => get<ActivityEntry[]>(`${bugPath(projectKey, n)}/activity`),
  });
}

// ───────────── Learnings & review ─────────────

export function useLearnings(
  projectKey: string,
  f: { q: string; category: RootCauseCategory | ""; escape: EscapePoint | ""; component: string },
) {
  const params = new URLSearchParams();
  if (f.q.trim()) params.set("q", f.q.trim());
  if (f.category) params.set("category", f.category);
  if (f.escape) params.set("escape", f.escape);
  if (f.component) params.set("component", f.component);
  const qs = params.toString();
  return useQuery({
    queryKey: ["bugs", projectKey, "learnings", f],
    queryFn: () => get<LearningEntry[]>(`/projects/${enc(projectKey)}/learnings${qs ? `?${qs}` : ""}`),
    placeholderData: (prev) => prev,
  });
}

export function useBugReview(projectKey: string) {
  return useQuery({
    queryKey: ["bugs", projectKey, "review"],
    queryFn: () => get<BugReview>(`/projects/${enc(projectKey)}/bug-review`),
  });
}

export function useOpenActionItems(projectKey: string) {
  return useQuery({
    queryKey: ["bugs", projectKey, "actionItems"],
    queryFn: () => get<ActionItemView[]>(`/projects/${enc(projectKey)}/action-items?open=true`),
  });
}

// ───────────── Regression suite ─────────────

export function useCreateRegressionSuite(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<TaskDetail>(`/projects/${enc(projectKey)}/regression`),
    onSuccess: (task) => {
      qc.setQueryData([...taskKey(projectKey, "REGRESSION"), "detail"], task);
      void qc.invalidateQueries({ queryKey: taskKey(projectKey, "REGRESSION") });
    },
  });
}

// ───────────── Sightings and regression flags ─────────────

/** Lives under the bug's query key, so bug changes and live events refresh it. */
export function useBugSightings(projectKey: string, n: number) {
  return useQuery({
    queryKey: [...bugKey(projectKey, n), "sightings"],
    queryFn: () => get<BugSightings>(`${bugPath(projectKey, n)}/sightings`),
  });
}

export function useSeenAgain(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: CreateSightingRequest) => post<BugSighting>(`${bugPath(projectKey, n)}/sightings`, body),
    onSuccess: () => invalidate(),
  });
}

export function useSightingActions(projectKey: string, n: number) {
  const qc = useQueryClient();
  const invalidate = useBugInvalidate(projectKey, n);
  const done = (sightings: BugSightings) => {
    qc.setQueryData([...bugKey(projectKey, n), "sightings"], sightings);
    invalidate();
  };
  // These change what a version holds or add one.
  const doneWithVersions = (sightings: BugSightings) => {
    done(sightings);
    void qc.invalidateQueries({ queryKey: ["releases", projectKey] });
  };
  return {
    setApp: useMutation({
      mutationFn: ({ id, app_id }: { id: string; app_id: string }) => put<BugSightings>(`${bugPath(projectKey, n)}/sightings/${enc(id)}/app`, { app_id } satisfies SetSightingAppRequest),
      onSuccess: done,
    }),
    toVersion: useMutation({
      mutationFn: (id: string) => post<BugSightings>(`${bugPath(projectKey, n)}/sightings/${enc(id)}/version`),
      onSuccess: doneWithVersions,
    }),
    reopen: useMutation({
      mutationFn: ({ id, note }: { id: string; note: string }) => post<BugSightings>(`${bugPath(projectKey, n)}/regressions/${enc(id)}/reopen`, { note } satisfies DecideRegressionRequest),
      onSuccess: doneWithVersions,
    }),
    dismiss: useMutation({
      mutationFn: ({ id, note }: { id: string; note: string }) => post<BugSightings>(`${bugPath(projectKey, n)}/regressions/${enc(id)}/dismiss`, { note } satisfies DecideRegressionRequest),
      onSuccess: done,
    }),
  };
}

// ───────────── GitLab: a bug's code ─────────────

const bugGitlabPath = (projectKey: string, n: number) => `${bugPath(projectKey, n)}/gitlab`;

/** Lives under the bug's query key, so bug changes and live events refresh it. */
export function useBugGitlab(projectKey: string, n: number) {
  return useQuery({
    queryKey: [...bugKey(projectKey, n), "gitlab"],
    queryFn: () => get<BugGitlabDetail>(bugGitlabPath(projectKey, n)),
  });
}

export function useAddBugGitlabLink(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (body: AddGitlabLinkRequest) => post<GitlabLinkView>(`${bugGitlabPath(projectKey, n)}/links`, body),
    onSuccess: () => invalidate(),
  });
}

export function useConfirmBugGitlabLink(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (linkId: string) => post<GitlabLinkView>(`${bugGitlabPath(projectKey, n)}/links/${enc(linkId)}/confirm`),
    onSuccess: () => invalidate(),
  });
}

export function useRemoveBugGitlabLink(projectKey: string, n: number) {
  const invalidate = useBugInvalidate(projectKey, n);
  return useMutation({
    mutationFn: (linkId: string) => del(`${bugGitlabPath(projectKey, n)}/links/${enc(linkId)}`),
    onSuccess: () => invalidate(),
  });
}
